/**
 * Brand Insights Component
 * Real data-driven recommendations and strategic insights
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, Image, Tags, Package, Camera, 
  CheckCircle, AlertCircle, Info, Sparkles, ArrowRight,
  Eye, Heart, Users, Bookmark, Clock
} from 'lucide-react';
import { useBrand } from '../../context/BrandContext';

export function BrandInsights() {
  const { authFetch, isAuthenticated, brand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState({
    profile: null,
    stats: null,
    products: [],
    insights: []
  });

  useEffect(() => {
    const fetchInsights = async () => {
      if (!isAuthenticated) return;

      try {
        const [profileRes, dashboardRes, productsRes] = await Promise.all([
          authFetch('/api/brand/profile/'),
          authFetch('/api/brand/dashboard/'),
          authFetch('/api/brand/products/?page=1&per_page=100')
        ]);

        const profile = profileRes.ok ? await profileRes.json() : {};
        const dashboard = dashboardRes.ok ? await dashboardRes.json() : {};
        const productsData = productsRes.ok ? await productsRes.json() : { products: [] };
        const products = productsData.products || [];
        const stats = dashboard.stats || {};

        // Generate insights based on real data
        const insights = generateInsights(profile, stats, products);
        console.log('BrandInsights Debug:', { profile, stats, productsCount: products.length, insightsCount: insights.length, insights });

        setInsightsData({
          profile,
          stats,
          products,
          insights
        });
      } catch (err) {
        console.error('Failed to fetch insights:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [isAuthenticated, authFetch]);

  // Generate real insights based on actual data
  const generateInsights = (profile, stats, products) => {
    const insights = [];

    // Profile completeness insights
    if (!profile.logo) {
      insights.push({
        type: 'action',
        priority: 'high',
        icon: Image,
        title: 'Add Your Logo',
        description: 'Brands with logos get 40% more visibility on the platform.',
        action: { label: 'Add Logo', path: '/brand-portal/profile' }
      });
    }

    if (!profile.description) {
      insights.push({
        type: 'action',
        priority: 'high',
        icon: Info,
        title: 'Add Brand Description',
        description: 'Tell your story to connect with customers.',
        action: { label: 'Add Description', path: '/brand-portal/profile' }
      });
    }

    if (!profile.instagram_handle) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Camera,
        title: 'Connect Instagram',
        description: 'Link your Instagram to build social proof.',
        action: { label: 'Connect', path: '/brand-portal/profile' }
      });
    }

    // Product insights
    if (stats.total_products === 0) {
      insights.push({
        type: 'action',
        priority: 'high',
        icon: Package,
        title: 'Add Your First Product',
        description: 'Start building your catalog to showcase on Yemberzal.',
        action: { label: 'Add Product', path: '/brand-portal/products/new' }
      });
    } else if (stats.total_products < 5) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Package,
        title: 'Expand Your Catalog',
        description: `You have ${stats.total_products} product${stats.total_products > 1 ? 's' : ''}. Brands with 10+ products see 2x more engagement.`,
        action: { label: 'Add More', path: '/brand-portal/products/new' }
      });
    }

    // Category diversity
    if (products.length > 0) {
      const categories = [...new Set(products.flatMap(p => p.categories || []))];
      if (categories.length === 0) {
        insights.push({
          type: 'suggestion',
          priority: 'medium',
          icon: Tags,
          title: 'Add Categories',
          description: 'Categorized products are 3x more likely to be discovered.',
          action: { label: 'Edit Products', path: '/brand-portal/collection' }
        });
      }
    }

    // Website sync insight
    if (profile.website && stats.scraped_products === 0) {
      insights.push({
        type: 'info',
        priority: 'low',
        icon: TrendingUp,
        title: 'Website Sync Available',
        description: 'Your website is connected. Products can be automatically synced.',
      });
    }

    // Engagement insights based on real data
    const productViews = stats.total_product_views || 0;
    const brandViews = stats.total_brand_views || 0;
    const productSaves = stats.total_product_saves || 0;
    const brandSaves = stats.total_brand_saves || 0;

    if (stats.total_products > 0 && productViews === 0) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Eye,
        title: 'Get More Product Views',
        description: 'Your products haven\'t received views yet. Share your brand page link on social media to drive traffic.',
      });
    }

    if (productViews > 0 && productSaves === 0) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Heart,
        title: 'Improve Save Rate',
        description: `Your products have ${productViews} views but no saves yet. Compelling descriptions and high-quality images increase saves.`,
      });
    }

    if (productViews > 0 && productSaves > 0) {
      const saveRate = ((productSaves / productViews) * 100).toFixed(1);
      if (saveRate > 5) {
        insights.push({
          type: 'achievement',
          priority: 'low',
          icon: Heart,
          title: 'Strong Save Rate',
          description: `${saveRate}% of viewers save your products — that's excellent engagement.`,
        });
      }
    }

    if (brandSaves > 0) {
      insights.push({
        type: 'achievement',
        priority: 'low',
        icon: Bookmark,
        title: `${brandSaves} Brand Follower${brandSaves > 1 ? 's' : ''}`,
        description: 'Users are bookmarking your brand. Keep adding products to retain their interest.',
      });
    }

    if (brandViews > 5 && brandSaves === 0) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Users,
        title: 'Convert Brand Visitors',
        description: `${brandViews} users visited your brand page. A complete profile with logo and description helps convert visitors to followers.`,
        action: { label: 'Update Profile', path: '/brand-portal/profile' }
      });
    }

    // Price analysis
    if (products.length >= 5) {
      const prices = products.map(p => parseFloat(p.price) || 0);
      const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
      const priceRange = Math.max(...prices) - Math.min(...prices);
      
      if (priceRange > avgPrice * 2) {
        insights.push({
          type: 'info',
          priority: 'low',
          icon: TrendingUp,
          title: 'Good Price Range',
          description: `Your products span from $${Math.min(...prices).toFixed(0)} to $${Math.max(...prices).toFixed(0)}, offering options for different budgets.`,
        });
      }
    }

    // Achievement insight for active brands
    if (stats.total_products >= 10 && profile.logo && profile.description) {
      insights.push({
        type: 'achievement',
        priority: 'low',
        icon: CheckCircle,
        title: 'Great Progress!',
        description: 'Your brand profile is well set up with a solid product catalog.',
      });
    }

    // Price range insights
    if (products.length >= 3) {
      const prices = products.map(p => parseFloat(p.price) || 0).filter(p => p > 0);
      if (prices.length > 0) {
        const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        
        // Check if prices are too similar
        const priceVariety = (maxPrice - minPrice) / avgPrice;
        if (priceVariety < 0.3) {
          insights.push({
            type: 'suggestion',
            priority: 'medium',
            icon: TrendingUp,
            title: 'Expand Price Range',
            description: `Your products range from $${minPrice.toFixed(0)} to $${maxPrice.toFixed(0)}. Consider adding products at different price points to reach more customers.`,
            action: { label: 'Add Products', path: '/brand-portal/products/new' }
          });
        }
        
        // Check if prices are too high for the market
        if (avgPrice > 300 && productViews > 0 && productSaves === 0) {
          insights.push({
            type: 'suggestion',
            priority: 'medium',
            icon: TrendingUp,
            title: 'Consider Lower Price Points',
            description: `Your average price is $${avgPrice.toFixed(0)} but you're not getting saves. Try adding products under $150 to attract more customers.`,
          });
        }
      }
    }

    // Product title and description quality insights
    const shortTitles = products.filter(p => p.title && p.title.length < 20);
    const shortDescriptions = products.filter(p => p.description && p.description.length < 50);
    
    if (shortTitles.length > 0 && shortTitles.length / products.length > 0.3) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Info,
        title: 'Improve Product Titles',
        description: `${shortTitles.length} of your products have short titles. Descriptive titles (20+ characters) improve search visibility.`,
        action: { label: 'Edit Products', path: '/brand-portal/collection' }
      });
    }
    
    if (shortDescriptions.length > 0 && shortDescriptions.length / products.length > 0.4) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Info,
        title: 'Enhance Product Descriptions',
        description: `${shortDescriptions.length} products need longer descriptions. Tell the story behind each piece to increase engagement.`,
        action: { label: 'Edit Products', path: '/brand-portal/collection' }
      });
    }

    // Image quality insights
    const productsWithoutImages = products.filter(p => !p.image || p.image.includes('placeholder') || p.image.includes('default'));
    if (productsWithoutImages.length > 0) {
      insights.push({
        type: 'action',
        priority: 'high',
        icon: Image,
        title: 'Add Product Images',
        description: `${productsWithoutImages.length} products are missing images. High-quality photos increase saves by 70%.`,
        action: { label: 'Add Images', path: '/brand-portal/collection' }
      });
    }

    // Engagement velocity insights
    if (productViews > 100 && productSaves === 0) {
      insights.push({
        type: 'suggestion',
        priority: 'high',
        icon: Heart,
        title: 'Low Save Rate Alert',
        description: `${productViews} views but no saves suggests your products need better images or more compelling descriptions.`,
        action: { label: 'Improve Listings', path: '/brand-portal/collection' }
      });
    }

    // Brand consistency insights
    if (products.length >= 5) {
      const inconsistentBranding = products.filter(p => {
        const hasTitle = p.title && p.title.trim().length > 0;
        const hasDescription = p.description && p.description.trim().length > 20;
        const hasImage = p.image && !p.image.includes('placeholder');
        return !(hasTitle && hasDescription && hasImage);
      });
      
      if (inconsistentBranding.length > products.length * 0.3) {
        insights.push({
          type: 'suggestion',
          priority: 'medium',
          icon: CheckCircle,
          title: 'Standardize Product Listings',
          description: `${inconsistentBranding.length} products need complete information (title, description, image) for consistent brand presentation.`,
          action: { label: 'Review Products', path: '/brand-portal/collection' }
        });
      }
    }

    // Recent activity insights
    if (stats.products_this_month === 0 && stats.total_products > 0) {
      insights.push({
        type: 'suggestion',
        priority: 'medium',
        icon: Clock,
        title: 'Add New Products',
        description: 'You haven\'t added products this month. Regular updates keep your brand fresh and engaging.',
        action: { label: 'Add Products', path: '/brand-portal/products/new' }
      });
    }

    // Social proof insights
    if (brandSaves > 10 && profile.instagram_handle) {
      insights.push({
        type: 'achievement',
        priority: 'low',
        icon: Camera,
        title: 'Strong Social Following',
        description: `${brandSaves} brand followers plus Instagram connection creates strong social proof.`,
      });
    }

    // Seasonal timing insights
    const currentMonth = new Date().getMonth();
    if (currentMonth >= 9 && currentMonth <= 11) { // Fall/Winter
      insights.push({
        type: 'info',
        priority: 'low',
        icon: TrendingUp,
        title: 'Peak Season Opportunity',
        description: 'Fall/Winter is peak season for Kashmiri fashion. Consider highlighting seasonal collections.',
      });
    }

    // Cross-selling opportunity
    if (products.length >= 3 && productSaves > 5) {
      insights.push({
        type: 'suggestion',
        priority: 'low',
        icon: Package,
        title: 'Create Product Collections',
        description: 'Group related products into collections to increase average order value and cross-selling.',
        action: { label: 'Organize Products', path: '/brand-portal/collection' }
      });
    }

    // Always add at least one insight if none were generated
    if (insights.length === 0) {
      insights.push({
        type: 'info',
        priority: 'low',
        icon: Info,
        title: 'Getting Started',
        description: 'Your brand is set up! As you add products and get engagement, we\'ll provide personalized recommendations.',
      });
    }

    // Add a test insight to verify rendering works
    insights.push({
      type: 'suggestion',
      priority: 'low',
      icon: Sparkles,
      title: 'Test Insight',
      description: 'This is a test insight to verify the rendering is working correctly.',
    });

    return insights;
  };

  const { profile, stats, products, insights } = insightsData;

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading insights...</span>
      </div>
    );
  }

  // Calculate profile completeness
  let completeness = 20;
  if (profile?.logo) completeness += 20;
  if (profile?.description) completeness += 20;
  if (profile?.instagram_handle) completeness += 15;
  if (profile?.website) completeness += 10;
  if (stats?.total_products > 0) completeness += 15;
  completeness = Math.min(completeness, 100);

  // Key metrics for cards — engagement-focused
  const keyMetrics = [
    {
      icon: Heart,
      title: 'Product Saves',
      value: (stats?.total_product_saves || 0).toLocaleString(),
      subtitle: 'Users saved your products',
      bg: '#3f3f46',
    },
    {
      icon: Bookmark,
      title: 'Brand Follows',
      value: (stats?.total_brand_saves || 0).toLocaleString(),
      subtitle: 'Users bookmarked your brand',
      bg: '#52525b',
    },
  ];

  // Separate insights by type
  const actionInsights = insights.filter(i => i.type === 'action');
  const suggestionInsights = insights.filter(i => i.type === 'suggestion' || i.type === 'info');
  const achievements = insights.filter(i => i.type === 'achievement');
  
  console.log('BrandInsights Render Debug:', { 
    totalInsights: insights.length, 
    actionInsights: actionInsights.length, 
    suggestionInsights: suggestionInsights.length, 
    achievements: achievements.length 
  });

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.label}>I N T E L L I G E N C E</div>
        <br />
        {/* <h1 style={styles.title}>Insights</h1> */}
        <p style={styles.subtitle}>Personalized recommendations for your brand</p>
      </div>

      {/* Key Engagement Metrics */}
      <div className="bp-metrics-grid" style={styles.metricsGrid}>
        {keyMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div 
              key={index} 
              style={{ ...styles.metricCard, background: metric.bg }}
            >
              <Icon style={styles.metricIcon} />
              <h3 style={styles.metricTitle}>{metric.title}</h3>
              <div style={styles.metricValue}>{metric.value}</div>
              <div style={styles.metricSubtitle}>{metric.subtitle}</div>
            </div>
          );
        })}
      </div>

      {/* Profile Completeness */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.label}>P R O F I L E</div>
          <h3 style={styles.cardTitle}>Brand Completeness</h3>
        </div>
        <div style={styles.completenessRow}>
          <span style={styles.completenessLabel}>Overall Score</span>
          <span style={styles.completenessValue}>{completeness}%</span>
        </div>
        <div style={styles.completenessBar}>
          <div style={{ 
            ...styles.completenessFill, 
            width: `${completeness}%`,
            background: completeness >= 80 ? '#22c55e' : 
                       completeness >= 50 ? '#f59e0b' : '#171717'
          }} />
        </div>
        
        <div className="bp-checklist-grid" style={styles.checklistGrid}>
          <div style={{
            ...styles.checkItem,
            borderColor: profile?.logo ? '#bbf7d0' : '#fecaca',
            background: profile?.logo ? '#f0fdf4' : '#fef2f2',
          }}>
            <div style={styles.checkLabel}>Logo</div>
            <div style={{ color: profile?.logo ? '#16a34a' : '#ef4444' }}>
              {profile?.logo ? '✓ Added' : '○ Missing'}
            </div>
          </div>
          <div style={{
            ...styles.checkItem,
            borderColor: profile?.description ? '#bbf7d0' : '#fecaca',
            background: profile?.description ? '#f0fdf4' : '#fef2f2',
          }}>
            <div style={styles.checkLabel}>Description</div>
            <div style={{ color: profile?.description ? '#16a34a' : '#ef4444' }}>
              {profile?.description ? '✓ Added' : '○ Missing'}
            </div>
          </div>
          <div style={{
            ...styles.checkItem,
            borderColor: profile?.instagram_handle ? '#bbf7d0' : '#e5e5e5',
            background: profile?.instagram_handle ? '#f0fdf4' : 'transparent',
          }}>
            <div style={styles.checkLabel}>Instagram</div>
            <div style={{ color: profile?.instagram_handle ? '#16a34a' : '#71717a' }}>
              {profile?.instagram_handle ? '✓ Connected' : '○ Optional'}
            </div>
          </div>
          <div style={{
            ...styles.checkItem,
            borderColor: stats?.total_products > 0 ? '#bbf7d0' : '#fecaca',
            background: stats?.total_products > 0 ? '#f0fdf4' : '#fef2f2',
          }}>
            <div style={styles.checkLabel}>Products</div>
            <div style={{ color: stats?.total_products > 0 ? '#16a34a' : '#ef4444' }}>
              {stats?.total_products > 0 ? `✓ ${stats.total_products} added` : '○ None yet'}
            </div>
          </div>
        </div>
      </div>

      {/* Action Items */}
      {actionInsights.length > 0 && (
        <div style={styles.insightsSection}>
          <div style={styles.sectionHeader}>
            <AlertCircle style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
            <div>
              <div style={styles.label}>P R I O R I T Y</div>
              <h3 style={styles.sectionTitle}>Action Required</h3>
            </div>
          </div>
          <div style={styles.insightsList}>
            {actionInsights.map((insight, index) => {
              const Icon = insight.icon;
              return (
                <div key={index} className="bp-insight-item" style={styles.insightItem}>
                  <div style={styles.insightIconWrap}>
                    <Icon style={{ width: '20px', height: '20px', color: '#171717' }} />
                  </div>
                  <div className="bp-insight-content" style={styles.insightContent}>
                    <h4 style={styles.insightTitle}>{insight.title}</h4>
                    <p style={styles.insightDesc}>{insight.description}</p>
                  </div>
                  {insight.action && (
                    <Link to={insight.action.path} className="bp-insight-action" style={styles.insightAction}>
                      {insight.action.label}
                      <ArrowRight style={{ width: '14px', height: '14px' }} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestionInsights.length > 0 && (
        <div style={styles.insightsSection}>
          <div style={styles.sectionHeader}>
            <Sparkles style={{ width: '20px', height: '20px', color: '#171717' }} />
            <div>
              <div style={styles.label}>R E C O M M E N D A T I O N S</div>
              <h3 style={styles.sectionTitle}>Suggestions</h3>
            </div>
          </div>
          <div style={styles.insightsList}>
            {suggestionInsights.map((insight, index) => {
              const Icon = insight.icon;
              return (
                <div key={index} className="bp-insight-item" style={styles.insightItem}>
                  <div style={{ ...styles.insightIconWrap, background: '#f5f5f5' }}>
                    <Icon style={{ width: '20px', height: '20px', color: '#71717a' }} />
                  </div>
                  <div className="bp-insight-content" style={styles.insightContent}>
                    <h4 style={styles.insightTitle}>{insight.title}</h4>
                    <p style={styles.insightDesc}>{insight.description}</p>
                  </div>
                  {insight.action && (
                    <Link to={insight.action.path} className="bp-insight-action" style={{ ...styles.insightAction, background: '#f5f5f5', color: '#171717' }}>
                      {insight.action.label}
                      <ArrowRight style={{ width: '14px', height: '14px' }} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Achievements */}
      {achievements.length > 0 && (
        <div style={styles.achievementBanner}>
          <CheckCircle style={{ width: '24px', height: '24px', color: '#22c55e' }} />
          <div>
            <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '600' }}>
              {achievements[0].title}
            </h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#71717a' }}>
              {achievements[0].description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  header: {
    marginBottom: '32px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  title: {
    fontSize: '42px',
    fontWeight: '400',
    color: '#171717',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '15px',
    color: '#71717a',
    margin: 0,
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '32px',
  },
  metricCard: {
    padding: '24px',
    borderRadius: '16px',
    color: '#ffffff',
  },
  metricIcon: {
    width: '24px',
    height: '24px',
    marginBottom: '16px',
    color: '#ffffff',
    stroke: '#ffffff',
    opacity: 0.8,
  },
  metricTitle: {
    fontSize: '13px',
    fontWeight: '500',
    margin: '0 0 8px 0',
    opacity: 0.7,
    color: '#ffffff',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: '500',
    marginBottom: '4px',
    color: '#ffffff',
  },
  metricSubtitle: {
    fontSize: '13px',
    opacity: 0.6,
    color: '#ffffff',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
  },
  cardHeader: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  completenessRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  completenessLabel: {
    fontSize: '14px',
    color: '#171717',
  },
  completenessValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#171717',
  },
  completenessBar: {
    height: '8px',
    background: '#f5f5f5',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  completenessFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  checklistGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
  },
  checkItem: {
    padding: '16px',
    border: '1px solid',
    borderRadius: '8px',
    fontSize: '13px',
  },
  checkLabel: {
    fontSize: '11px',
    color: '#a1a1aa',
    marginBottom: '4px',
  },
  insightsSection: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 32px',
    borderBottom: '1px solid #e5e5e5',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  insightsList: {
    padding: '8px 0',
  },
  insightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 32px',
    borderBottom: '1px solid #f5f5f5',
  },
  insightIconWrap: {
    width: '44px',
    height: '44px',
    background: '#fef3c7',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  insightContent: {
    flex: 1,
  },
  insightTitle: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#171717',
    margin: '0 0 4px 0',
  },
  insightDesc: {
    fontSize: '14px',
    color: '#71717a',
    margin: 0,
    lineHeight: 1.5,
  },
  insightAction: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    background: '#171717',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '500',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  achievementBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '24px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '16px',
  },
};

export default BrandInsights;
